import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError, subscribeToJob } from "@hostdock/sdk";
import type { DnsNameserverSettings, JobLogRecord, JobRecord, SecurityPolicy } from "@hostdock/types";
import {
  Button, DataTable, Dialog, EmptyState, ErrorState, Field, FormError, formatDateTime, formatRelative, humanize, Input, KeyValue, Notice,
  PageHeader, Pagination, Panel, Select, Skeleton, StatusBadge, Switch, Tabs, toast,
} from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useSession } from "../session";

type Tab = "security" | "runtimes" | "dns" | "jobs";

export function SettingsPage() {
  const { can } = useSession();
  const tabs: { value: Tab; label: string }[] = [];
  if (can("SETTINGS_MANAGE") || can("SECURITY_MANAGE")) tabs.push({ value: "security", label: "Security policy" });
  if (can("SETTINGS_MANAGE") || can("SECURITY_MANAGE")) tabs.push({ value: "runtimes", label: "Runtimes" });
  if (can("SETTINGS_MANAGE")) tabs.push({ value: "dns", label: "DNS" });
  if (can("MONITORING_VIEW")) tabs.push({ value: "jobs", label: "Background jobs" });
  const [tab, setTab] = useState<Tab>(tabs[0]?.value ?? "security");
  return (
    <RequirePermission anyOf={["SETTINGS_MANAGE", "SECURITY_MANAGE", "MONITORING_VIEW"]}>
      <PageHeader title="Settings" description="Platform-wide configuration." />
      {tabs.length > 1 && <Tabs<Tab> value={tab} onChange={setTab} items={tabs} />}
      {tab === "security" ? <SecurityPolicyForm /> : tab === "runtimes" ? <RuntimesForm /> : tab === "dns" ? <NameserversForm /> : <JobsPanel />}
    </RequirePermission>
  );
}

function SecurityPolicyForm() {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const [draft, setDraft] = useState<SecurityPolicy | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.security);
  }, [q.data]);
  const editable = can("SETTINGS_MANAGE") && can("SECURITY_MANAGE");
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: (security: SecurityPolicy) => api.settings.update({ security }),
    onSuccess: (s) => {
      qc.setQueryData(["settings"], s);
      toast.success("Security policy saved");
    },
  });

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Skeleton className="h-80 w-full" />;
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const num = (k: keyof SecurityPolicy) => (e: { target: { value: string } }) => setDraft({ ...draft, [k]: Number(e.target.value) });
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data?.security);

  return (
    <div className="flex max-w-3xl flex-col gap-5">
      {!editable && <Notice>Changing the security policy requires both SETTINGS_MANAGE and SECURITY_MANAGE.</Notice>}
      <Panel title="Two-factor authentication">
        <div className="flex flex-col gap-4">
          <Switch checked={draft.adminMfaRequired} onChange={(v) => setDraft({ ...draft, adminMfaRequired: v })} disabled={!editable} label="Require for admins" description="Admins without two-factor must enroll before they can use HostDock Admin." />
          <Switch checked={draft.customerMfaRequired} onChange={(v) => setDraft({ ...draft, customerMfaRequired: v })} disabled={!editable} label="Require for customers" description="Customers without two-factor must enroll at their next sign-in." />
        </div>
      </Panel>
      <Panel title="Sessions">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Idle timeout (minutes)" hint="Sign out after this long without activity." error={fe["security.sessionIdleMinutes"]}>
            {(p) => <Input {...p} type="number" min={5} max={1440} value={draft.sessionIdleMinutes} onChange={num("sessionIdleMinutes")} disabled={!editable} />}
          </Field>
          <Field label="Maximum session length (hours)" hint="Sign out after this long regardless of activity." error={fe["security.sessionAbsoluteHours"]}>
            {(p) => <Input {...p} type="number" min={1} max={720} value={draft.sessionAbsoluteHours} onChange={num("sessionAbsoluteHours")} disabled={!editable} />}
          </Field>
        </div>
      </Panel>
      <Panel title="Brute-force protection">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Failed attempts before lockout" error={fe["security.loginMaxAttempts"]}>
            {(p) => <Input {...p} type="number" min={3} max={50} value={draft.loginMaxAttempts} onChange={num("loginMaxAttempts")} disabled={!editable} />}
          </Field>
          <Field label="Lockout duration (minutes)" error={fe["security.lockoutMinutes"]}>
            {(p) => <Input {...p} type="number" min={1} max={1440} value={draft.lockoutMinutes} onChange={num("lockoutMinutes")} disabled={!editable} />}
          </Field>
        </div>
      </Panel>
      {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
      {editable && (
        <div className="flex gap-2">
          <Button variant="primary" loading={save.isPending} disabled={!dirty} onClick={() => save.mutate(draft)}>Save policy</Button>
          <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(q.data!.security)}>Discard changes</Button>
        </div>
      )}
    </div>
  );
}

function RuntimesForm() {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const [draft, setDraft] = useState<Record<"php" | "node" | "python", string> | null>(null);
  useEffect(() => {
    if (q.data) setDraft({ php: q.data.runtimes.php.join(", "), node: q.data.runtimes.node.join(", "), python: q.data.runtimes.python.join(", ") });
  }, [q.data]);
  const editable = can("SETTINGS_MANAGE") && can("SERVER_PROVISION");
  const split = (v: string) => v.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
  const save = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => api.settings.update({ runtimes: { php: split(d.php), node: split(d.node), python: split(d.python) } }),
    onSuccess: (s) => {
      qc.setQueryData(["settings"], s);
      toast.success("Runtime versions saved");
    },
  });
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Skeleton className="h-60 w-full" />;
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  return (
    <div className="flex max-w-3xl flex-col gap-5">
      <Notice title="Maintained runtime versions">
        These images are pre-pulled and verified on every node during provisioning (and on Update). Remove versions that reach end of life; existing
        websites keep running until they are moved to a supported version.
      </Notice>
      {!editable && <Notice>Changing runtimes requires SETTINGS_MANAGE and SERVER_PROVISION.</Notice>}
      <Panel title="Versions">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="PHP" hint="php:<v>-fpm" error={fe["runtimes.php.0"] ?? fe["runtimes.php"]}>{(p) => <Input {...p} value={draft.php} onChange={(e) => setDraft({ ...draft, php: e.target.value })} disabled={!editable} className="font-mono" />}</Field>
          <Field label="Node.js" hint="node:<v>-bookworm-slim" error={fe["runtimes.node.0"] ?? fe["runtimes.node"]}>{(p) => <Input {...p} value={draft.node} onChange={(e) => setDraft({ ...draft, node: e.target.value })} disabled={!editable} className="font-mono" />}</Field>
          <Field label="Python" hint="python:<v>-slim" error={fe["runtimes.python.0"] ?? fe["runtimes.python"]}>{(p) => <Input {...p} value={draft.python} onChange={(e) => setDraft({ ...draft, python: e.target.value })} disabled={!editable} className="font-mono" />}</Field>
        </div>
      </Panel>
      {save.error && !Object.keys(fe).length ? <FormError error={save.error} /> : null}
      {editable && (
        <div>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(draft)}>Save versions</Button>
        </div>
      )}
    </div>
  );
}

type DnsMode = "cluster" | "per_server" | "cloudns";
type SyncView = { status: "OK" | "FAILED" | "PENDING"; at: string | null; error: string | null; zones: number | null };

function SyncLine({ sync }: { sync: SyncView }) {
  if (sync.status === "OK") return <span className="text-success">Serving {sync.zones} zone{sync.zones === 1 ? "" : "s"}{sync.at ? ` (updated ${formatRelative(sync.at)})` : ""}.</span>;
  if (sync.status === "FAILED") return <span className="text-danger">Last update failed{sync.at ? ` ${formatRelative(sync.at)}` : ""}: {sync.error}</span>;
  return <span className="text-muted">Waiting for the first update.</span>;
}

const HOSTNAME_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}$/;
const splitHosts = (v: string) => v.split(/[,\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);

/** The form's editable copy of the saved DNS settings. */
function dnsDraft(d: DnsNameserverSettings) {
  return {
    mode: d.mode,
    cluster: d.custom ? d.nameservers.map((n) => ({ hostname: n.hostname, serverId: n.serverId })) : [],
    perServer: Object.fromEntries(d.perServer.map((p) => [p.serverId, p.hostnames.join(", ")])) as Record<string, string>,
    cdns: { authType: d.cloudns?.authType ?? ("auth-id" as const), authId: d.cloudns?.authId ?? "", password: "", nameservers: (d.cloudns?.nameservers ?? []).join(", ") },
  };
}

/**
 * How customer domains get their DNS: one HostDock DNS cluster, each server's own nameservers (like WHM), or ClouDNS.
 * Customers edit their records in the Panel's Zone Editor in every mode.
 */
function NameserversForm() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings-dns"], queryFn: api.settings.dns, refetchInterval: 15_000 });
  const [mode, setMode] = useState<DnsMode | null>(null);
  const [cluster, setCluster] = useState<{ hostname: string; serverId: string }[] | null>(null);
  const [perServer, setPerServer] = useState<Record<string, string> | null>(null);
  const [cdns, setCdns] = useState<{ authType: "auth-id" | "sub-auth-id" | "sub-auth-user"; authId: string; password: string; nameservers: string } | null>(null);
  const reset = (d: DnsNameserverSettings) => {
    const x = dnsDraft(d);
    setMode(x.mode); setCluster(x.cluster); setPerServer(x.perServer); setCdns(x.cdns);
  };
  useEffect(() => {
    if (!q.data || mode !== null) return;
    const x = dnsDraft(q.data);
    setMode(x.mode); setCluster(x.cluster); setPerServer(x.perServer); setCdns(x.cdns);
  }, [q.data, mode]);
  const save = useMutation({
    mutationFn: () => api.settings.saveDns({
      mode: mode!,
      nameservers: cluster ?? [],
      perServer: Object.fromEntries(Object.entries(perServer ?? {}).map(([id, v]) => [id, splitHosts(v)])),
      ...(cdns && (mode === "cloudns" || cdns.authId) ? { cloudns: { authType: cdns.authType, authId: cdns.authId, ...(cdns.password ? { password: cdns.password } : {}), nameservers: splitHosts(cdns.nameservers) } } : {}),
    }),
    onSuccess: (d) => { qc.setQueryData(["settings-dns"], d); reset(d); toast.success("DNS settings saved; zones are being published"); },
  });
  const test = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.settings.testCloudns({ authType: cdns!.authType, authId: cdns!.authId, ...(cdns!.password ? { password: cdns!.password } : {}) }),
    onSuccess: () => toast.success("ClouDNS accepted these credentials"),
  });
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data || !mode || !cluster || !perServer || !cdns) return <Skeleton className="h-60 w-full" />;
  const d = q.data;
  const servers = d.servers;
  const byId = new Map(servers.map((s) => [s.id, s]));
  const fe = save.error instanceof ApiError ? save.error.fieldErrors : {};
  const clusterValid = cluster.every((n) => HOSTNAME_RE.test(n.hostname) && n.serverId);
  const perServerValid = Object.values(perServer).every((v) => splitHosts(v).every((h) => HOSTNAME_RE.test(h))) && (mode !== "per_server" || Object.values(perServer).some((v) => splitHosts(v).length));
  const cdnsValid = mode !== "cloudns" || (!!cdns.authId && (!!cdns.password || !!d.cloudns?.passwordSet) && splitHosts(cdns.nameservers).length > 0 && splitHosts(cdns.nameservers).every((h) => HOSTNAME_RE.test(h)));
  const MODES: { value: DnsMode; title: string; body: string }[] = [
    { value: "cluster", title: "DNS cluster", body: "A shared set of HostDock servers answers for every customer zone. Use your own ns1/ns2 names, or let HostDock use its servers' hostnames automatically." },
    { value: "per_server", title: "Nameservers per server", body: "Like WHM: each server has its own nameservers and answers for the domains hosted on it." },
    { value: "cloudns", title: "ClouDNS", body: "HostDock pushes every zone and record to your ClouDNS account over its API; ClouDNS's nameservers answer." },
  ];
  return (
    <div className="flex max-w-4xl flex-col gap-5">
      <Notice title="Customer DNS">
        Every domain a customer adds has a DNS zone with its records (A, www, mail, MX, SPF, DMARC and, with email, DKIM), which they edit in Panel → Zone
        Editor. Choose who answers DNS for those zones. HostDock servers answer with PowerDNS (like cPanel), installed by the
        "DNS server" provisioning step. Customers are only shown nameservers once they answer.
      </Notice>
      <Panel title="How customer zones are served">
        <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="DNS mode">
          {MODES.map((m) => (
            <button key={m.value} type="button" role="radio" aria-checked={mode === m.value} onClick={() => setMode(m.value)}
              className={`rounded-lg border p-3 text-left transition ${mode === m.value ? "border-accent bg-accent-soft ring-1 ring-accent" : "border-line hover:border-line-strong"}`}>
              <span className="block text-sm font-semibold">{m.title}{d.mode === m.value ? " · in use" : ""}</span>
              <span className="mt-1 block text-xs text-muted">{m.body}</span>
            </button>
          ))}
        </div>
      </Panel>

      {mode === "cluster" && (
        <>
          {!d.custom && (
            <Panel title="In use now (automatic)">
              {d.nameservers.length ? (
                <ul className="flex flex-col gap-1 text-sm">
                  {d.nameservers.map((n) => <li key={n.serverId}><span className="font-mono">{n.hostname}</span> <span className="text-muted">· {n.ip}</span> <SyncLine sync={n.sync} /></li>)}
                </ul>
              ) : (
                <p className="text-sm text-muted">No server qualifies yet. Run More → Update HostDock components on your servers, and make sure each server's hostname has a public A record pointing at its IP. Until then, customers' zones are saved but not published.</p>
              )}
            </Panel>
          )}
          <Panel title="Your own cluster nameservers (optional)">
            <div className="flex flex-col gap-3">
              <p className="text-xs text-muted">Create each hostname with the server's IP address at your own domain's registrar (glue records if they are inside that domain).</p>
              {cluster.length === 0 && <p className="text-sm text-muted">None: the automatic nameservers are used.</p>}
              {cluster.map((n, i) => {
                const status = d.custom ? d.nameservers.find((x) => x.hostname === n.hostname && x.serverId === n.serverId) : undefined;
                const srv = byId.get(n.serverId);
                return (
                  <div key={i} className="flex flex-col gap-2 rounded-lg border border-line p-3">
                    <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                      <Field label={`Nameserver ${i + 1}`} error={fe[`nameservers.${i}.hostname`]}>
                        {(p) => <Input {...p} value={n.hostname} placeholder={`ns${i + 1}.yourcompany.com`} onChange={(e) => setCluster(cluster.map((x, j) => (j === i ? { ...x, hostname: e.target.value.trim().toLowerCase() } : x)))} className="font-mono" />}
                      </Field>
                      <Field label="Answered by">
                        {(p) => (
                          <Select {...p} value={n.serverId} onChange={(e) => setCluster(cluster.map((x, j) => (j === i ? { ...x, serverId: e.target.value } : x)))}>
                            <option value="">Choose a server…</option>
                            {servers.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.publicIp}{s.dnsReady ? "" : " (needs update)"}</option>)}
                          </Select>
                        )}
                      </Field>
                      <Button variant="ghost" onClick={() => setCluster(cluster.filter((_x, j) => j !== i))}>Remove</Button>
                    </div>
                    {srv && (
                      <p className="text-xs text-muted">
                        Point <span className="font-mono">{n.hostname || "the hostname"}</span> at <span className="font-mono">{srv.publicIp}</span>.{" "}
                        {!srv.dnsReady && <span className="text-warning">PowerDNS is not set up on this server yet: run More → Update HostDock components on it. </span>}
                        {status && <SyncLine sync={status.sync} />}
                      </p>
                    )}
                  </div>
                );
              })}
              {cluster.length < 6 && <div><Button onClick={() => setCluster([...cluster, { hostname: "", serverId: servers[0]?.id ?? "" }])}>Add nameserver</Button></div>}
            </div>
          </Panel>
        </>
      )}

      {mode === "per_server" && (
        <Panel title="Nameservers of each server">
          <div className="flex flex-col gap-3">
            <p className="text-xs text-muted">A domain uses the nameservers of the server its websites run on. Give each server one or more hostnames (comma-separated), each with an A record to that server's IP at your registrar. Servers left empty serve no DNS.</p>
            {d.perServer.length === 0 && <p className="text-sm text-muted">No servers yet. Add one in Servers first.</p>}
            {d.perServer.map((p) => (
              <div key={p.serverId} className="grid gap-2 rounded-lg border border-line p-3 sm:grid-cols-[200px_1fr] sm:items-start">
                <div className="text-sm">
                  <div className="font-semibold">{p.serverName}</div>
                  <div className="font-mono text-xs text-muted">{p.ip}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <Field label={`Nameservers of ${p.serverName}`}>
                    {(fp) => <Input {...fp} value={perServer[p.serverId] ?? ""} placeholder={`ns1.${p.serverName.toLowerCase()}.yourcompany.com, ns2…`} onChange={(e) => setPerServer({ ...perServer, [p.serverId]: e.target.value })} className="font-mono" />}
                  </Field>
                  <p className="text-xs text-muted">
                    {!p.dnsReady && <span className="text-warning">PowerDNS is not set up on this server yet: run More → Update HostDock components. </span>}
                    {p.hostnames.length > 0 && d.mode === "per_server" && <SyncLine sync={p.sync} />}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {mode === "cloudns" && (
        <Panel title="ClouDNS account">
          <div className="flex flex-col gap-3">
            <p className="text-xs text-muted">In ClouDNS, create an API user (API &amp; Resellers → API users) and allow this control plane's IP address. HostDock creates a master zone for every customer domain and keeps its records in line; the records stay editable in the Zone Editor.</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Login type">
                {(p) => (
                  <Select {...p} value={cdns.authType} onChange={(e) => setCdns({ ...cdns, authType: e.target.value as typeof cdns.authType })}>
                    <option value="auth-id">API user ID (auth-id)</option>
                    <option value="sub-auth-id">Sub-user ID (sub-auth-id)</option>
                    <option value="sub-auth-user">Sub-user name (sub-auth-user)</option>
                  </Select>
                )}
              </Field>
              <Field label="API user" error={fe["cloudns.authId"]}>{(p) => <Input {...p} value={cdns.authId} onChange={(e) => setCdns({ ...cdns, authId: e.target.value.trim() })} autoComplete="off" />}</Field>
              <Field label="API password" hint={d.cloudns?.passwordSet ? "Saved. Leave empty to keep it." : undefined}>
                {(p) => <Input {...p} type="password" value={cdns.password} onChange={(e) => setCdns({ ...cdns, password: e.target.value })} autoComplete="new-password" />}
              </Field>
            </div>
            <Field label="Nameservers customers should use" hint="As shown in your ClouDNS account, e.g. ns1.cloudns.net, ns2.cloudns.net (or your vanity/premium ones)." error={fe["cloudns.nameservers"]}>
              {(p) => <Input {...p} value={cdns.nameservers} onChange={(e) => setCdns({ ...cdns, nameservers: e.target.value })} placeholder="ns1.cloudns.net, ns2.cloudns.net" className="font-mono" />}
            </Field>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Button onClick={() => test.mutate()} loading={test.isPending} disabled={!cdns.authId || (!cdns.password && !d.cloudns?.passwordSet)}>Test connection</Button>
              {d.cloudns && d.mode === "cloudns" && <SyncLine sync={d.cloudns.sync} />}
            </div>
            {test.error ? <FormError error={test.error} /> : null}
          </div>
        </Panel>
      )}

      {save.error ? <FormError error={save.error} /> : null}
      <div className="flex gap-2">
        <Button variant="primary" loading={save.isPending} disabled={!clusterValid || !perServerValid || !cdnsValid} onClick={() => save.mutate()}>Save DNS settings</Button>
        <Button variant="ghost" onClick={() => reset(d)}>Discard changes</Button>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

function JobsPanel() {
  const { can } = useSession();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["jobs", page], queryFn: () => api.jobs.list({ page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData, refetchInterval: 10_000 });
  const run = useMutation({
    mutationFn: api.jobs.runMaintenanceCleanup,
    onSuccess: (job) => {
      setOpenId(job.id);
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <Panel
        title="Background jobs"
        description="Long-running operations run in the worker. Maintenance cleanup also runs automatically every hour."
        actions={can("SETTINGS_MANAGE") && <Button size="sm" icon={<Play size={13} />} loading={run.isPending} onClick={() => run.mutate()}>Run maintenance cleanup</Button>}
        flush
      >
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(j) => j.id}
          onRowClick={(j) => setOpenId(j.id)}
          empty={<EmptyState icon={ListChecks} title="No jobs have run yet" description="Jobs appear here once the worker schedules or runs them." />}
          columns={[
            { key: "type", header: "Job", cell: (j) => <span className="font-medium">{humanize(j.type)}</span> },
            { key: "status", header: "Status", cell: (j) => <StatusBadge status={j.status} /> },
            { key: "progress", header: "Progress", cell: (j) => <span className="tabular-nums">{j.progress}%</span>, hideOnMobile: true },
            { key: "attempts", header: "Attempts", cell: (j) => <span className="tabular-nums">{j.attempts}/{j.maxAttempts}</span>, hideOnMobile: true },
            { key: "created", header: "Created", cell: (j) => <span className="text-muted" title={formatDateTime(j.createdAt)}>{formatRelative(j.createdAt)}</span> },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={setPage} />}
      </Panel>
      {openId && <JobDialog id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

/** Live job view: initial state from the API, then updates over Server-Sent Events. */
function JobDialog({ id, onClose }: { id: string; onClose(): void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["job", id], queryFn: () => api.jobs.get(id) });
  const [live, setLive] = useState<{ job: JobRecord | null; logs: JobLogRecord[] }>({ job: null, logs: [] });
  const [streamError, setStreamError] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToJob(
      api.jobs.eventsUrl(id),
      (e) => {
        setStreamError(false);
        setLive((prev) => ({ job: e.job, logs: e.log && !prev.logs.some((l) => l.id === e.log!.id) ? [...prev.logs, e.log] : prev.logs }));
        if (["SUCCESS", "FAILED", "CANCELLED"].includes(e.job.status)) void qc.invalidateQueries({ queryKey: ["jobs"] });
      },
      () => setStreamError(true),
    );
    return unsubscribe;
  }, [id, qc]);

  const job = live.job ?? detail.data?.job;
  const logs = [...(detail.data?.logs ?? [])];
  for (const l of live.logs) if (!logs.some((x) => x.id === l.id)) logs.push(l);

  const act = useMutation({ meta: { inlineError: true },
    mutationFn: (kind: "cancel" | "retry") => (kind === "cancel" ? api.jobs.cancel(id) : api.jobs.retry(id)),
    onSuccess: (j) => {
      setLive((p) => ({ ...p, job: j }));
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      void qc.invalidateQueries({ queryKey: ["job", id] });
    },
  });
  const manage = can("SETTINGS_MANAGE");

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={job ? humanize(job.type) : "Job"}
      footer={
        <>
          {manage && job && ["QUEUED", "WAITING", "RUNNING"].includes(job.status) && !job.cancelRequested && (
            <Button variant="danger-ghost" loading={act.isPending} onClick={() => act.mutate("cancel")}>Cancel job</Button>
          )}
          {manage && job && ["FAILED", "CANCELLED"].includes(job.status) && (
            <Button loading={act.isPending} onClick={() => act.mutate("retry")}>Retry</Button>
          )}
          <Button variant="primary" onClick={onClose}>Close</Button>
        </>
      }
    >
      {detail.error ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : !job ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <div className="mb-1.5 flex items-center justify-between text-[13px]">
              <StatusBadge status={job.status} />
              <span className="text-muted tabular-nums">{job.progress}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-valuenow={job.progress} aria-valuemin={0} aria-valuemax={100}>
              <div className={`h-full transition-all ${job.status === "FAILED" ? "bg-danger" : "bg-accent"}`} style={{ width: `${job.progress}%` }} />
            </div>
            {job.progressMessage && <p className="mt-1.5 text-[12.5px] text-muted">{job.progressMessage}</p>}
          </div>
          {job.error && <Notice tone={job.status === "FAILED" ? "danger" : "warning"} title={job.status === "FAILED" ? "Job failed" : "Last attempt failed"}>{job.error}</Notice>}
          {streamError && !["SUCCESS", "FAILED", "CANCELLED"].includes(job.status) && <Notice tone="warning">Live updates interrupted. Reconnecting…</Notice>}
          <KeyValue
            items={[
              { label: "Job ID", value: <span className="font-mono text-[12px]">{job.id}</span> },
              { label: "Attempts", value: `${job.attempts} of ${job.maxAttempts}` },
              { label: "Created", value: formatDateTime(job.createdAt) },
              { label: "Started", value: formatDateTime(job.startedAt) },
              { label: "Finished", value: formatDateTime(job.finishedAt) },
              { label: "Request ID", value: <span className="font-mono text-[12px]">{job.correlationId ?? "—"}</span> },
            ]}
          />
          <div>
            <p className="mb-1.5 text-[13px] font-medium">Log</p>
            <div className="max-h-64 overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-[12px]">
              {logs.length === 0 ? (
                <span className="text-subtle">No log output yet.</span>
              ) : (
                logs.map((l) => (
                  <div key={l.id} className={l.level === "ERROR" ? "text-danger" : l.level === "WARN" ? "text-warning" : undefined}>
                    <span className="text-subtle">{new Date(l.createdAt).toLocaleTimeString()} </span>
                    {l.message}
                  </div>
                ))
              )}
            </div>
          </div>
          <FormError error={act.error} />
        </div>
      )}
    </Dialog>
  );
}
